import { md5 } from '@core/utils/md5';
import { getLanguage } from '@core/i18n/language';
import {
  readPersistedLoginUsername,
  useAuth,
} from '@core/gameengine/state/auth';
import { getRegulatoryPolicy } from '@core/regulatory';
import { resolvePublicUrl } from '@core/utils/assetFolderSlug';
import {
  getApiUrlFromGameQuery,
  getLobbyUrlFromGameQuery,
} from '@config/runtime.config';

/** Shared secret used by slotengine / logs UI for account hash_code. */
export const LOGS_PRIVATE_KEY = 'pzvlxpvrctiypcpbrstpcpxcyadhrg';

const PROJECT_LOGS_PAGE_PATH = '/logs/index.html';

export function createLogsAccountHash(accountId: string): string {
  return md5(accountId + LOGS_PRIVATE_KEY);
}

/**
 * Login / operator account id used for session logs (never display name or Player-xxxx).
 */
export function resolveLogsAccountId(): string | null {
  const fromLogin = readPersistedLoginUsername().trim();
  if (fromLogin) return fromLogin;

  const loginUsername = useAuth.getState().user?.loginUsername?.trim();
  if (loginUsername && !/^Player-/i.test(loginUsername)) {
    return loginUsername;
  }

  return null;
}

function resolveLogsPageBaseUrl(): string {
  const configured = import.meta.env.VITE_LOGS_URL?.trim();
  if (configured) return configured;

  const path = resolvePublicUrl(PROJECT_LOGS_PAGE_PATH);
  if (typeof window !== 'undefined' && path.startsWith('/')) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

function resolveJurisdiction(): string {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get(
      'jurisdiction'
    );
    if (fromQuery) return fromQuery;
  } catch {
    /* ignore */
  }
  const policyJurisdiction = getRegulatoryPolicy().jurisdiction;
  if (
    policyJurisdiction &&
    policyJurisdiction !== 'standard' &&
    policyJurisdiction !== 'DEFAULT'
  ) {
    return policyJurisdiction;
  }
  return '';
}

export type BuildGameLogsUrlOptions = {
  accountId?: string;
  language?: string;
  jurisdiction?: string;
  lobbyUrl?: string;
  /** Backend endpoint from game launch `?apiUrl=` — used by logs for API calls. */
  apiUrl?: string;
  baseUrl?: string;
};

/**
 * Builds session-logs URL:
 * `{base}?account_id={id}&language={lang}&jurisdiction={j}&hash_code={md5(id+key)}[&lobbyUrl=…][&apiUrl=…]`
 * `lobbyUrl` / `apiUrl` are only appended when present on the game URL (or passed explicitly).
 */
export function buildGameLogsUrl(
  options: BuildGameLogsUrlOptions = {}
): string | null {
  const accountId = (options.accountId ?? resolveLogsAccountId())?.trim();
  if (!accountId) return null;

  const language = options.language ?? getLanguage();
  const jurisdiction = options.jurisdiction ?? resolveJurisdiction();
  const baseUrl = options.baseUrl ?? resolveLogsPageBaseUrl();
  const lobbyUrl = (options.lobbyUrl ?? getLobbyUrlFromGameQuery())?.trim();
  const apiUrl = (options.apiUrl ?? getApiUrlFromGameQuery())?.trim();

  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(baseUrl, origin);
  url.searchParams.set('account_id', accountId);
  url.searchParams.set('language', language);
  url.searchParams.set('jurisdiction', jurisdiction);
  url.searchParams.set('hash_code', createLogsAccountHash(accountId));
  if (lobbyUrl) {
    url.searchParams.set('lobbyUrl', lobbyUrl);
  }
  if (apiUrl) {
    url.searchParams.set('apiUrl', apiUrl.replace(/\/$/, ''));
  }
  return url.toString();
}

export function openGameLogs(options: BuildGameLogsUrlOptions = {}): boolean {
  // Re-read launch query overrides at click time.
  const lobbyUrl = options.lobbyUrl ?? getLobbyUrlFromGameQuery();
  const apiUrl = options.apiUrl ?? getApiUrlFromGameQuery();
  const url = buildGameLogsUrl({ ...options, lobbyUrl, apiUrl });
  if (!url) return false;
  console.log('[PlayGrid] Opening logs URL:', url);
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}
